'use client';

import { ChevronRight, Megaphone } from 'lucide-react';
import { openErrorFeedback } from './error-feedback';

interface ReportProblemButtonProps {
  /** Wird vor dem Öffnen des Formulars aufgerufen — schließt das jeweilige Menü. */
  onActivate?: () => void;
  /**
   * `'card'` — große Karte mit Beschreibung (Mobile-BottomSheet).
   * `'slim'` — schlanke Zeile im Stil der Nav-Einträge (Desktop-Sidebar-Footer).
   */
  variant?: 'card' | 'slim';
}

/**
 * "Fehler melden"-Eintrag: schließt erst das jeweilige Menü (falls `onActivate`
 * gesetzt) und öffnet dann das Sentry-Feedback-Formular. Ersetzt den früheren
 * schwebenden Sentry-Button.
 */
export function ReportProblemButton({ onActivate, variant = 'card' }: ReportProblemButtonProps) {
  const handleClick = () => {
    onActivate?.();
    void openErrorFeedback();
  };

  if (variant === 'slim') {
    // Desktop-Sidebar: gemütete Zeile analog der inaktiven Nav-Einträge.
    return (
      <button
        type="button"
        onClick={handleClick}
        className="w-full flex items-start gap-3 px-4 py-2.5 rounded-lg text-left text-text-muted hover:bg-zinc-100 hover:text-secondary transition-colors"
      >
        <Megaphone size={20} className="shrink-0 mt-0.5" aria-hidden="true" />
        <span className="min-w-0">
          <span className="block text-sm font-bold">Fehler melden</span>
          <span className="block text-xs text-text-muted mt-0.5">
            Etwas funktioniert nicht? Sagen Sie uns Bescheid.
          </span>
        </span>
      </button>
    );
  }

  // Mobile-Sheet: vollbreite Karte mit Icon, Titel und Beschreibung.
  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full flex items-center gap-3 rounded-xl border border-zinc-200 p-4 text-left hover:bg-zinc-50 transition-colors"
    >
      <Megaphone size={18} className="shrink-0 text-zinc-500" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-zinc-900">Fehler melden</span>
        <span className="block text-xs text-zinc-500 mt-0.5">
          Etwas funktioniert nicht wie erwartet? Schicken Sie uns einen kurzen Bericht.
        </span>
      </span>
      <ChevronRight size={16} className="shrink-0 text-zinc-400" aria-hidden="true" />
    </button>
  );
}
